/**
 * Reports what GAM actually served into each Squares ad slot, to Sentry.
 *
 * ### Why this exists
 *
 * The anchored footer creative has been reported cut off on iOS since #699, and every
 * attempt to fix it (#699 → #755, reverted in #756) was tuned from screenshots because
 * the embed can only be observed in production. Screenshots cannot separate the two
 * explanations that look identical from outside GAM's iframe:
 *
 *   - the slot was filled at a size too small for the creative (our problem — the sizes
 *     we offer, in `FooterAdvert`), or
 *   - the slot was filled at the right size and the creative *asset* inside it is built
 *     taller than the size it is trafficked at (ad-ops' problem, and nothing we ship can
 *     fix it).
 *
 * DK report that Google is serving 320x50, and this module is how that gets confirmed
 * rather than argued: `slotRenderEnded` carries the size GAM chose, straight from
 * Google, plus the creative and line-item ids. The size settles the first explanation.
 * The creative id is the key ad-ops need to pull that asset's real dimensions out of
 * GAM, which settles the second.
 *
 * The asset's own height is NOT measurable from here and this module does not pretend
 * to: the creative renders in a cross-origin iframe. What we can measure is the frame
 * around it, which is why `renderedFrame` below is the DOM box and `servedSize` is
 * Google's own report — a disagreement between those two is itself worth seeing.
 *
 * ### What the first prod reports settled, and what they left open
 *
 * Both size explanations are now dead. The 19 Aug reports off `prod-v1.5.172` came back
 * with the offered sizes, `servedSize`, `renderedFrame` and — once ad-ops sent the asset
 * over — the creative file itself all agreeing at 320x50. Nothing in the chain is the
 * wrong size, so the banner is not being clipped by a frame too small for it. It is
 * being *covered*: a correct 320x50 element renders and DK's native tab bar is painted
 * over its lower half. Screenshots cannot tell those two apart, which is what made
 * #699 → #755 a sequence of guesses.
 *
 * That moves the question from size to position, which is why `renderedFrame` carries
 * `bottom` and why `frameBottomGapPx` exists. The open question is now whether our
 * `--squares-embed-bottom-chrome` offset reaches the element at all, and the standing
 * suspicion is that it does — and that its hardcoded 36px simply understates DK's real
 * overlap, because `bottomInsetSource` keeps coming back `absent`.
 *
 * ### Volume
 *
 * One Sentry event per unique (slot, served size, creative) per session — see
 * `reportedKeys`. Repeat impressions of a creative already reported add a breadcrumb
 * only, which costs nothing until the session errors. The point is to learn which
 * creatives are in rotation at which sizes, and that needs each one once, not each
 * impression.
 *
 * ### Lifetime
 *
 * Diagnostic, not permanent. Once ad-ops have confirmed the trafficked asset against
 * the ids this produces, delete the module and its call in `App.tsx`.
 */

import * as Sentry from '@sentry/react';
import { getDefinedSlotConfig, onAnySlotRenderEnded, type SlotRenderEndedEvent } from './gpt';
import {
  describeEmbedBottomChrome,
  type EmbedPlatform,
  type EmbedBottomChromeReport,
  type EmbedBottomChromeSource,
} from './embedChrome';

/** Sentry message every report shares, so they group into one issue per fingerprint. */
const REPORT_MESSAGE = 'GAM slot render';

/**
 * Unique (slot, size, creative) combinations already reported this page load.
 *
 * Deliberately not persisted: a fresh session should re-report, because the whole
 * question is which creatives appear across sessions and devices.
 */
const reportedKeys = new Set<string>();

const formatSize = (size: SlotRenderEndedEvent['size']): string => {
  if (typeof size === 'string') return size;
  if (Array.isArray(size)) return `${size[0]}x${size[1]}`;
  return 'none';
};

const formatSizes = (sizes: [number, number][] | undefined): string =>
  sizes?.length ? sizes.map(([w, h]) => `${w}x${h}`).join(', ') : 'none';

/** The rendered ad frame's box — size and position, both ours to measure. */
export interface RenderedFrame {
  width: number;
  height: number;
  /** The frame's bottom edge in viewport coordinates. */
  bottom: number;
}

/**
 * Measure the rendered ad frame, which is ours to measure — unlike the creative inside.
 *
 * Position is recorded as well as size because size alone has stopped being able to
 * explain the defect: the first prod report (19 Aug) came back with the offered sizes,
 * the served size, this frame and the creative asset all agreeing at 320x50. Nothing
 * being the wrong size rules out a clipped frame and leaves the banner being *covered*
 * by DK's tab bar — and whether our own offset was applied as intended is only visible
 * from where the frame sits.
 */
const measureRenderedFrame = (divId: string): RenderedFrame | null => {
  if (typeof document === 'undefined') return null;
  const iframe = document.getElementById(divId)?.querySelector('iframe');
  if (!iframe) return null;
  const { width, height, bottom } = iframe.getBoundingClientRect();
  return { width: Math.round(width), height: Math.round(height), bottom: Math.round(bottom) };
};

/**
 * How far the frame's bottom edge sits above the bottom of the viewport.
 *
 * The number that separates the two remaining explanations. It should come back as the
 * resolved embed chrome plus the banner's own vertical padding; a materially different
 * value means our offset never reached the element and the bug is ours. A value that
 * matches while the creative still reads as cut off means the offset landed and DK's
 * chrome is simply taller than the 36px we guess at.
 *
 * It cannot see the tab bar — that is native, outside the webview — so a matching gap
 * is evidence for occlusion, never proof of it.
 */
const measureFrameBottomGapPx = (frame: RenderedFrame | null): number | null => {
  if (!frame || typeof window === 'undefined') return null;
  return Math.round(window.innerHeight - frame.bottom);
};

export interface AdRenderReport {
  divId: string;
  adUnitPath: string | null;
  isEmpty: boolean;
  /** The size GAM chose, as Google reports it. */
  servedSize: string;
  /** The sizes the slot offered it to choose from. */
  offeredSizes: string;
  /** Whether a responsive size mapping was in play for this slot. */
  hasSizeMapping: boolean;
  /** The DOM box of the injected frame — compare against servedSize. */
  renderedFrame: RenderedFrame | null;
  /**
   * Gap in px between the frame's bottom edge and the bottom of the viewport.
   *
   * Compare against `embedChromePx` plus the banner's own padding: agreement means the
   * offset applied and the creative is being covered by chrome taller than we assume;
   * disagreement means the offset never reached the element, which is ours to fix.
   */
  frameBottomGapPx: number | null;
  creativeId: number | null;
  lineItemId: number | null;
  advertiserId: number | null;
  campaignId: number | null;
  sourceAgnosticCreativeId: number | null;
  sourceAgnosticLineItemId: number | null;
  isBackfill: boolean;
  viewport: string;
  devicePixelRatio: number;
  /**
   * Whether DK sent a `?bottomInset`, and what became of it.
   *
   * Carried on every ad report because the two questions are one question in practice:
   * a creative can look cut off either because GAM framed it too small or because the
   * banner is sitting under DK's tab bar, and reading the served size without knowing
   * how the banner was positioned has already produced two reverted fixes.
   */
  bottomInsetSource: EmbedBottomChromeSource;
  bottomInsetRaw: string | null;
  bottomInsetAppliedPx: number | null;
  /** What the banner actually positioned against — override, stylesheet, or fallback. */
  embedChromePx: number | null;
  /**
   * `env(safe-area-inset-bottom)` as the platform reports it.
   *
   * Carried because it is the only component of the overlap that might be measurable
   * from inside the webview at all. It cannot see DK's tab bar, so a reading of 0 is
   * not evidence of no overlap — but a non-zero one is evidence our frame extends into
   * space the platform already considers covered.
   */
  safeAreaInsetBottomPx: number | null;
  /**
   * Whether the chrome was reserved for DK's native app or for a plain web iframe.
   *
   * The entire reserve hangs off this one classification, and it is a user-agent sniff
   * rather than a measurement, so it is reported on every render — a footer ad wrong in
   * either direction should be one filter away from its cause.
   */
  embedContext: 'dk-native' | 'browser';
  /** Which per-platform clearance row this session resolved to. */
  embedPlatform: EmbedPlatform;
}

/** Flatten one GPT event plus what we know locally into a flat, Sentry-shaped record. */
export const buildAdRenderReport = (event: SlotRenderEndedEvent): AdRenderReport => {
  const divId = event.slot.getSlotElementId();
  const config = getDefinedSlotConfig(divId);
  const response = event.slot.getResponseInformation?.() ?? null;
  const embedChrome = describeEmbedBottomChrome();
  const renderedFrame = measureRenderedFrame(divId);

  const pick = (
    fromEvent: number | null | undefined,
    fromResponse: number | null | undefined
  ): number | null => fromEvent ?? fromResponse ?? null;

  return {
    divId,
    adUnitPath: config?.adUnitPath ?? event.slot.getAdUnitPath?.() ?? null,
    isEmpty: event.isEmpty,
    servedSize: formatSize(event.size),
    offeredSizes: formatSizes(config?.sizes),
    hasSizeMapping: (config?.sizeMappings?.length ?? 0) > 0,
    renderedFrame,
    frameBottomGapPx: measureFrameBottomGapPx(renderedFrame),
    creativeId: pick(event.creativeId, response?.creativeId),
    lineItemId: pick(event.lineItemId, response?.lineItemId),
    advertiserId: pick(event.advertiserId, response?.advertiserId),
    campaignId: pick(event.campaignId, response?.campaignId),
    sourceAgnosticCreativeId: pick(
      event.sourceAgnosticCreativeId,
      response?.sourceAgnosticCreativeId
    ),
    sourceAgnosticLineItemId: pick(
      event.sourceAgnosticLineItemId,
      response?.sourceAgnosticLineItemId
    ),
    isBackfill: event.isBackfill ?? false,
    bottomInsetSource: embedChrome.source,
    bottomInsetRaw: embedChrome.paramRaw,
    bottomInsetAppliedPx: embedChrome.appliedPx,
    embedChromePx: embedChrome.resolvedPx,
    safeAreaInsetBottomPx: embedChrome.safeAreaInsetBottomPx,
    embedContext: embedChrome.embedContext,
    embedPlatform: embedChrome.embedPlatform,
    viewport:
      typeof window === 'undefined' ? 'unknown' : `${window.innerWidth}x${window.innerHeight}`,
    devicePixelRatio: typeof window === 'undefined' ? 1 : window.devicePixelRatio,
  };
};

/** What makes a report worth an event rather than just a breadcrumb. */
export const reportKey = (report: AdRenderReport): string =>
  `${report.divId}|${report.servedSize}|${report.creativeId ?? 'none'}`;

/**
 * Send one report. A breadcrumb always; a full event only the first time this
 * (slot, size, creative) combination is seen this session.
 */
export const reportAdRender = (event: SlotRenderEndedEvent): void => {
  const report = buildAdRenderReport(event);

  Sentry.addBreadcrumb({
    category: 'ad',
    level: 'info',
    message: `${report.divId} ${report.isEmpty ? 'empty' : report.servedSize}`,
    data: { ...report },
  });

  const key = reportKey(report);
  if (reportedKeys.has(key)) return;
  reportedKeys.add(key);

  Sentry.withScope(scope => {
    scope.setLevel('info');
    scope.setContext('ad', { ...report });

    // Tags are what make this filterable in Sentry without opening each event —
    // "show me every render of the sticky footer that came back 320x50".
    scope.setTag('ad.slot', report.divId);
    scope.setTag('ad.served_size', report.servedSize);
    scope.setTag('ad.offered_sizes', report.offeredSizes);
    scope.setTag('ad.creative_id', String(report.creativeId ?? 'none'));
    scope.setTag('ad.line_item_id', String(report.lineItemId ?? 'none'));
    scope.setTag('ad.is_empty', String(report.isEmpty));

    // The positioning half. `absent` here is the standing answer to "has DK shipped
    // the ?bottomInset contract yet"; `rejected` means they have and we are binning it.
    scope.setTag('embed.bottom_inset_source', report.bottomInsetSource);
    scope.setTag('embed.chrome_px', String(report.embedChromePx ?? 'unknown'));
    scope.setTag('ad.frame_bottom_gap_px', String(report.frameBottomGapPx ?? 'unknown'));
    scope.setTag('embed.safe_area_bottom_px', String(report.safeAreaInsetBottomPx ?? 'unknown'));
    scope.setTag('embed.context', report.embedContext);
    scope.setTag('embed.platform', report.embedPlatform);

    // One issue per slot+size+creative rather than one per session, so the Sentry
    // issue list reads as the set of creatives in rotation.
    scope.setFingerprint([REPORT_MESSAGE, report.divId, report.servedSize, key]);

    Sentry.captureMessage(`${REPORT_MESSAGE}: ${report.divId} ${report.servedSize}`);
  });
};

/**
 * Tag every Sentry event from this session with how the bottom inset resolved.
 *
 * Set globally rather than only on ad reports so unrelated errors and replays carry it
 * too — and so the answer to "did DK send one" survives even in a session where no ad
 * ever filled.
 *
 * Call after `applyEmbedBottomChromeOverride`, so the applied value is already in the
 * cascade for `resolvedPx` to read back.
 */
export const reportEmbedBottomChrome = (): EmbedBottomChromeReport => {
  const report = describeEmbedBottomChrome();

  Sentry.setTag('embed.bottom_inset_source', report.source);
  Sentry.setTag('embed.bottom_inset_raw', report.paramRaw ?? 'none');
  Sentry.setTag('embed.chrome_px', String(report.resolvedPx ?? 'unknown'));
  Sentry.setTag('embed.safe_area_bottom_px', String(report.safeAreaInsetBottomPx ?? 'unknown'));
  Sentry.setTag('embed.context', report.embedContext);
  Sentry.setTag('embed.platform', report.embedPlatform);
  Sentry.setContext('embed', { ...report });

  return report;
};

/**
 * Start reporting. Returns an unsubscribe function.
 *
 * Safe to call before GPT has loaded — the subscription goes through `googletag.cmd`,
 * which drains once the library is live.
 */
export const startAdRenderReporting = (): (() => void) => onAnySlotRenderEnded(reportAdRender);

/** Test seam: forget what has been reported so far. */
export const resetAdRenderReporting = (): void => reportedKeys.clear();
